import { DateOnly, format } from '@if/core/date'
import { formatCurrency } from '@if/core/utils'
import {
  Caption,
  Icon,
  type ColumnDef,
  type ColumnVisibilityModel,
  type GetRowStyle,
  type RowPalette,
  type RowStyle,
  useCan,
} from '@if/mui'
import { access } from '@src/access'
import { Box, Tooltip } from '@mui/material'
import { PaymentMethodIconComponent } from '@src/components/LoanCalendar/PaymentMethodIconComponent'
import { EBankFailureCode, EFeeType, EOperationStatus } from '@src/types/constants'
import { CustomerPaymentOperation, EOperationType, OperationInformation } from '@src/types/loan/loan-operation'
import { CalendarSimulation, getCancelledFee, getEditableFee, getFee } from '@src/types/loan/loan-simulation'
import { CircleCheck, CircleX, Clock, Pencil, RotateCcw, Trash2 } from 'lucide-react'
import { ReactNode, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLoanLock } from '@src/providers/LoanLockProvider'
import { useGetOutstandingBalance } from '../LoanCalendar-hooks'
import { ELoanCalendarAction, useLoanCalendarAction } from '../LoanCalendarActionProvider'
import { OperationTypeIconComponent } from '../OperationTypeIconComponent'

export type LoanCalendarRow = OperationInformation

const DETAIL_COLUMN_NAMES = [
  'interestPaid',
  'principalPaid',
  'nsfPaid',
  'otherFeePaid',
  'advancedPrincipalAmount',
  'principalBalance',
] as const

const blankWhenZero = (value: number | null | undefined) => value || null

const displayNegativeAmountAsZero = (value: number) => (value < 0 ? 0 : value)

/** The total of an invoice's lines; null on a row that is not an invoice. */
const getInvoiceTotal = (row: LoanCalendarRow): number | null =>
  row.operation._internalType === EOperationType.InvoiceOperation
    ? row.operation.debtInvoices.reduce((total, line) => total + line.amount, 0)
    : null

// An invoice row splits what it billed by money pool (the backend maps each product to its pool).
const isInvoice = (row: LoanCalendarRow) => row.operation._internalType === EOperationType.InvoiceOperation

// A charge-off row is deleted through its own action, never edited.
const isChargeOff = (row: LoanCalendarRow) => row.operation._internalType === EOperationType.ChargeOffOperation

// A fee row's text takes the colour of its type, the way the status column colours its icon: an NSF fee reads as
// the failure behind it, a general fee as information.
const FEE_TEXT_PALETTE: Record<EFeeType, RowPalette> = {
  [EFeeType.NsfFees]: 'error',
  [EFeeType.GeneralFees]: 'info',
}

const formatCellValue = (type: ColumnDef<LoanCalendarRow>['type'], value: unknown): string => {
  if (type === 'currencydecimal') return formatCurrency(value as number)
  if (type === 'dateonly') return format(value as DateOnly, 'P')
  return String(value)
}

/**
 * Colours the text of a fee row's cells. The cell keeps its type's formatting; any other row keeps the type's own
 * rendering (getFormatted returning undefined). Cells that render themselves (icons, actions) are left alone.
 */
const withFeeTextColor = (column: ColumnDef<LoanCalendarRow>): ColumnDef<LoanCalendarRow> => {
  if (column.type === 'action' || column.renderCell) return column

  return {
    ...column,
    getFormatted: (value: unknown, row: LoanCalendarRow) => {
      const fee = getFee(row.operation)
      if (!fee) return undefined
      return (
        <Box component="span" color={`${FEE_TEXT_PALETTE[fee.feeType]}.main`}>
          {formatCellValue(column.type, value)}
        </Box>
      )
    },
  } as ColumnDef<LoanCalendarRow>
}

export const useLoanCalendarTableColumns = (): ColumnDef<LoanCalendarRow>[] => {
  const { t } = useTranslation()
  const { setSelectedPaymentIds, dispatchAction } = useLoanCalendarAction()
  const { isReadOnly } = useLoanLock()
  const canPerformChargeOff = useCan(access.performChargeOff)

  const displayCorrectPaymentStatusIcon = useCallback(
    (operationStatus: EOperationStatus, bankFailureCode: EBankFailureCode | null, bankFailureDate: string | null) => {
      let content: ReactNode
      let tooltipTitle: string = t(`loan.payment.pmtStatus.${operationStatus}`)

      const isFailed = operationStatus === EOperationStatus.Failed
      const hasFailureDetails = isFailed && bankFailureCode

      if (hasFailureDetails) {
        tooltipTitle += ` - ${t(`enum.bankFailureCode.${bankFailureCode}`)}`
        content = (
          <Box display="flex" flexDirection="column" alignItems="center">
            <Caption sx={{ color: 'error.main' }}>{bankFailureCode}</Caption>
            <Caption sx={{ color: 'error.main' }}>{bankFailureDate}</Caption>
          </Box>
        )
      } else if (isFailed) {
        content = <Icon icon={CircleX} color="error" />
      } else if (operationStatus === EOperationStatus.Planned) {
        content = <Icon icon={Clock} color="action" />
      } else if (operationStatus === EOperationStatus.Complete) {
        content = <Icon icon={CircleCheck} color="success" />
      } else {
        content = <Icon icon={Clock} color="warning" />
      }

      return (
        <Tooltip title={tooltipTitle}>
          <Box display="inline-flex" alignItems="center">
            {content}
          </Box>
        </Tooltip>
      )
    },
    [t],
  )

  const dispatchOnRow = useCallback(
    (action: ELoanCalendarAction) => (row: LoanCalendarRow) => {
      setSelectedPaymentIds(new Set([row.simulatedOperationId]))
      dispatchAction(action)
    },
    [setSelectedPaymentIds, dispatchAction],
  )

  return useMemo<ColumnDef<LoanCalendarRow>[]>(() => {
    const columns: ColumnDef<LoanCalendarRow>[] = [
      {
        name: 'paymentNumber',
        type: 'number',
        header: '#',
        width: 50,
        align: 'center',
        hideable: false,
        getValue: (row) => getCustomerOperationValueFromRow(row, 'paymentNumber'),
      },
      {
        name: 'paymentMethod',
        type: 'number',
        header: t('loan.payment.pmtModeTitle'),
        width: 60,
        align: 'center',
        hideable: false,
        getValue: (row) => getCustomerOperationValueFromRow(row, 'paymentMethodId'),
        // A customer payment shows how it is paid; any other operation, what kind it is.
        renderCell: (row) =>
          row.operation._internalType === EOperationType.CustomerPaymentOperation ? (
            <PaymentMethodIconComponent paymentMode={row.operation.paymentMethodId} />
          ) : (
            <OperationTypeIconComponent operationType={row.operation._internalType} />
          ),
      },
      {
        name: 'plannedDate',
        type: 'dateonly',
        header: t('loan.payment.plannedOnDate'),
        hideable: false,
        getValue: (row) => row.operation.effectiveDate,
      },
      {
        name: 'amount',
        type: 'currencydecimal',
        header: t('loan.payment.amount'),
        hideable: false,
        getValue: (row) => (isInvoice(row) ? getInvoiceTotal(row) : getCustomerOperationValueFromRow(row, 'amount')),
      },
      {
        name: 'status',
        type: 'number',
        header: t('loan.payment.status'),
        width: 120,
        align: 'center',
        hideable: false,
        getValue: (row) => row.operation.operationStatusId,
        renderCell: (row) =>
          displayCorrectPaymentStatusIcon(
            row.operation.operationStatusId ?? EOperationStatus.Planned,
            getCustomerOperationValueFromRow(row, 'bankFailureCode'),
            getCustomerOperationValueFromRow(row, 'bankFailureDate'),
          ),
      },
      {
        name: 'interestPaid',
        type: 'currencydecimal',
        header: t('loan.payment.plannedInterestAmount'),
        getValue: (row) => {
          if (isInvoice(row)) return blankWhenZero(row.operationResultDto.interestInvoiced)
          return row.operation._internalType === EOperationType.CustomerPaymentOperation
            ? row.operationResultDto.interestPaid
            : null
        },
      },
      {
        name: 'principalPaid',
        type: 'currencydecimal',
        header: t('loan.payment.principalAmount'),
        getValue: (row) => {
          if (isInvoice(row)) return blankWhenZero(row.operationResultDto.principalInvoiced)
          return row.operation._internalType === EOperationType.CustomerPaymentOperation
            ? row.operationResultDto.principalPaid
            : null
        },
      },
      {
        name: 'nsfPaid',
        type: 'currencydecimal',
        header: t('loan.payment.nsfFee'),
        getValue: (row) =>
          blankWhenZero(isInvoice(row) ? row.operationResultDto.nsfInvoiced : row.operationResultDto.nsfPaid),
      },
      {
        name: 'otherFeePaid',
        type: 'currencydecimal',
        header: t('loan.payment.otherFee'),
        getValue: (row) =>
          blankWhenZero(isInvoice(row) ? row.operationResultDto.otherFeeInvoiced : row.operationResultDto.otherFeePaid),
      },
      {
        name: 'advancedPrincipalAmount',
        type: 'currencydecimal',
        header: t('loan.payment.advancedPrincipalAmount'),
        getValue: () => null, // TODO: Implement data
      },
      {
        name: 'principalBalance',
        type: 'currencydecimal',
        header: t('loan.payment.principalBalance'),
        getValue: (row) => row.operationResultDto.principalBalance,
      },
      {
        name: 'overdueAmount',
        type: 'currencydecimal',
        header: t('loan.payment.overdueAmount'),
        hideable: false,
        getValue: (row) => blankWhenZero(displayNegativeAmountAsZero(row.lateInformationResult.pastdue)),
      },
      {
        name: 'latePrincipal',
        type: 'currencydecimal',
        header: t('loan.payment.preview.latePrincipal'),
        hideable: false,
        getValue: (row) => blankWhenZero(displayNegativeAmountAsZero(row.lateInformationResult?.overduePrincipal ?? 0)),
      },
      {
        name: 'loanBalance',
        type: 'currencydecimal',
        header: t('loan.payment.loanBalance'),
        hideable: false,
        getValue: (row) => row.operationResultDto.loanBalance,
      },
      {
        name: 'description',
        header: t('loan.payment.description'),
        minWidth: 250,
        wrap: true,
        hideable: false,
        getValue: (row) => row.operation.note || '',
      },
      {
        name: 'action',
        type: 'action',
        header: t('common.menu'),
        actions: [
          {
            icon: Pencil,
            label: t('loan.payment.options.edit'),
            hidden: (row) => getCancelledFee(row.operation) !== null || isChargeOff(row),
            // A fee row opens the fee form, the same as adding one; every other row opens the payments editor.
            onClick: (row) =>
              getEditableFee(row.operation)
                ? dispatchOnRow(ELoanCalendarAction.OpenEditFeeDialog)(row)
                : dispatchOnRow(ELoanCalendarAction.OpenEditPaymentsDialog)(row),
          },
          {
            // A cancelled fee is reapplied rather than edited.
            icon: RotateCcw,
            label: t('loan.payment.options.reapply'),
            hidden: (row) => getCancelledFee(row.operation) === null,
            onClick: dispatchOnRow(ELoanCalendarAction.ReapplyFee),
          },
          {
            icon: Trash2,
            label: t('loan.payment.options.delete'),
            palette: () => 'error',
            disabled: (row) => row.operation.operationStatusId === EOperationStatus.Cancelled,
            hidden: (row) => isChargeOff(row),
            // A fee is cancelled, never deleted; every other row opens the payments editor.
            onClick: (row) =>
              getEditableFee(row.operation)
                ? dispatchOnRow(ELoanCalendarAction.OpenCancelFeeDialog)(row)
                : dispatchOnRow(ELoanCalendarAction.OpenDeletePaymentsDialog)(row),
          },
          {
            icon: Trash2,
            label: t('loan.payment.options.deleteChargeOff'),
            palette: () => 'error',
            hidden: (row) => !isChargeOff(row) || !canPerformChargeOff,
            onClick: dispatchOnRow(ELoanCalendarAction.OpenDeleteChargeOffDialog),
          },
        ],
      },
    ]
    // Someone else holds the loan: no row action can apply, so the row menu is dropped rather than greyed out.
    const visibleColumns = isReadOnly ? columns.filter((column) => column.name !== 'action') : columns
    return visibleColumns.map(withFeeTextColor)
  }, [t, displayCorrectPaymentStatusIcon, dispatchOnRow, isReadOnly, canPerformChargeOff])
}

export const useDetailColumnsVisibility = (showMore: boolean): ColumnVisibilityModel =>
  useMemo(() => Object.fromEntries(DETAIL_COLUMN_NAMES.map((name) => [name, showMore])), [showMore])

const getRowColor = (row: LoanCalendarRow) => {
  const operationType = row.operation._internalType
  if (operationType === EOperationType.CustomerPaymentOperation && row.modifiedByUser()) return 'warning'
  return undefined
}

export const useLoanCalendarRowStyle = (
  simulation: CalendarSimulation,
): GetRowStyle<LoanCalendarRow, RowStyle<LoanCalendarRow>> => {
  const lastPaymentOn = simulation.fullLoan.loan.lastPaymentOn
  const outstandingBalance = useGetOutstandingBalance(simulation)
  const repaymentConfigDates = useMemo(() => simulation.getAllLoanRepaymentConfigDates(), [simulation])
  const scheduleEndPalette: RowPalette = outstandingBalance > 0 ? 'warning' : 'success'

  const frequencyIndex = useCallback(
    (date: DateOnly) => {
      for (let i = repaymentConfigDates.length - 1; i >= 0; i--) {
        if (date.getTime() >= repaymentConfigDates[i].getTime()) return i
      }
      return 0
    },
    [repaymentConfigDates],
  )

  return useCallback(
    (row) => ({
      palette: getRowColor(row),
      boundary: [
        { group: row.operation.effectiveDate > lastPaymentOn, palette: scheduleEndPalette, sorted: null },
        { group: frequencyIndex(row.operation.effectiveDate), palette: 'info', sorted: null },
      ],
    }),
    [lastPaymentOn, scheduleEndPalette, frequencyIndex],
  )
}

type CustomerOperationKey = keyof CustomerPaymentOperation
type CustomerOperationValue<K extends CustomerOperationKey> = CustomerPaymentOperation[K]

const getCustomerOperationValueFromRow = <K extends CustomerOperationKey>(
  row: LoanCalendarRow,
  columnName: K,
): CustomerOperationValue<K> =>
  (columnName in row.operation
    ? (row.operation as Record<CustomerOperationKey, unknown>)[columnName]
    : undefined) as CustomerOperationValue<K>
